import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "../../../configuration";
import { EditIssueNoteDto, FetchError, GetIssuesDto, IssueDto, UserDto } from "../../types";
import { useAccessToken } from "../../use-access-token";
import { queryKeys } from "../queryKeys";
import { useSignalR } from "../../../hooks";
import { optimisticallyUpdateQueryData } from "../utilities";

type Variables = {
  issueGuid: string;
  teamSlug?: string;
  note: string;
  createdByUser: UserDto;
};

const useEditIssueNoteRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editIssueNoteRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: EditIssueNoteDto = {
      note: variables.note,
    };

    const response = await fetch(`${configuration.apiRootUrl}/issues/${variables.issueGuid}/note`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editIssueNoteRequest };
};

const useEditIssueNote = () => {
  const { editIssueNoteRequest } = useEditIssueNoteRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: editIssueNoteRequest,
    onMutate: async (variables) => {
      let cachedTeamIssuesData: GetIssuesDto | undefined;
      let cachedUserIssuesData: GetIssuesDto | undefined;
      let cachedIssueData: IssueDto | undefined;

      if (variables.teamSlug) {
        cachedTeamIssuesData = await optimisticallyUpdateQueryData<GetIssuesDto>(
          queryClient,
          queryKeys.issues.getIssues(variables.teamSlug, undefined),
          (cachedData) => ({
            ...cachedData,
            issues: cachedData.issues.map((issue) =>
              issue.guid === variables.issueGuid
                ? {
                    ...issue,
                    note: variables.note,
                  }
                : issue
            ),
          })
        );
      }

      cachedUserIssuesData = await optimisticallyUpdateQueryData<GetIssuesDto>(
        queryClient,
        queryKeys.issues.getIssues(undefined, variables.createdByUser.userId),
        (cachedData) => ({
          ...cachedData,
          issues: cachedData.issues.map((issue) =>
            issue.guid === variables.issueGuid
              ? {
                  ...issue,
                  note: variables.note,
                }
              : issue
          ),
        })
      );

      cachedIssueData = await optimisticallyUpdateQueryData<IssueDto>(
        queryClient,
        queryKeys.issues.getIssueDetail(variables.issueGuid),
        (cachedData) => ({
          ...cachedData,
          note: variables.note,
        })
      );

      return { cachedTeamIssuesData, cachedUserIssuesData, cachedIssueData };
    },
    onError: (_error, variables, context) => {
      if (variables.teamSlug) {
        queryClient.setQueryData(
          queryKeys.issues.getIssues(variables.teamSlug, undefined),
          context?.cachedTeamIssuesData
        );
      }

      queryClient.setQueryData(
        queryKeys.issues.getIssues(undefined, variables.createdByUser.userId),
        context?.cachedUserIssuesData
      );
      queryClient.setQueryData(queryKeys.issues.getIssueDetail(variables.issueGuid), context?.cachedIssueData);
    },
    onSuccess: (_data, variables) => {
      if (variables.teamSlug) {
        signalR.sendRefetchTeamIssues(variables.teamSlug);
      }
      if (variables.createdByUser) {
        signalR.sendRefetchUserIssues(variables.createdByUser.userId);
      }
      signalR.sendRefetchIssue(variables.issueGuid);
      signalR.sendRefetchComments(variables.issueGuid);
    },
    onSettled: (_data, _error, variables) => {
      if (variables.teamSlug) {
        queryClient.invalidateQueries(queryKeys.issues.getIssues(variables.teamSlug, undefined));
      }
      if (variables.createdByUser) {
        queryClient.invalidateQueries(queryKeys.issues.getIssues(undefined, variables.createdByUser.userId));
      }
      queryClient.invalidateQueries(queryKeys.issues.getIssueDetail(variables.issueGuid));
      queryClient.invalidateQueries(queryKeys.issues.getIssueFeed(variables.issueGuid));
    },
  });
};

export { useEditIssueNote };
